'use strict';

/*
This code uses the Autocomplete widget to help the user select a
place, then it retrieves the address components associated with that
place, and then it populates the form fields with those details.

To use this module:
* call the `init` method of this module (currently done for checkout forms
    only in .../js/pages/checkout/index and /billingshipping)
* include .../templates/default/components/googleplacesautocomplete.isml in your template
* add classes to your form's input fields
    *  the main input which should show the autocomplete dropdown (add unique id too!):
            .gpac-input.gpac-field
    *  the fields of the address "parts" (e.g. zip, city, ...):
            .gpac-field.gpac-field-address1
            (replace _address1_ accordingly, full list -> see "config" object below)
    *  may also be combined, e.g. we're using address1 as main input currently:
            .gpac-input.gpac-field.gpac-field-address1

Notes:
* Only visible input fields will be initialised. If you hide forms or load them via AJAX,
    initialise them manually on-show by calling _initAutocompleteForArea($parentArea)_ or
    _initInputField(inputField)_ manually.
    In this case, make sure you only initalise it once (or when necessary), as every init-call
    is billed by Google as a new session (see billingshipping.js for reference).
*/

var $ = require('jquery');
import {debounce} from './custom-lodash';

// store autocomplete objects for updating
var autocompleteMap = {};

var $addres1WithStreetNo = $('.gpac-field-address1-streetNo');

// response mapping
var config = {
    street_number: {
        value: 'short_name',
        selector: $addres1WithStreetNo.length ? '.gpac-field-address1-streetNo' : '.gpac-field-streetNo'
    },
    route: {
        // street name
        value: 'long_name',
        selector: $addres1WithStreetNo.length ? '.gpac-field-address1-streetNo' : '.gpac-field-address1'
        // use '.gpac-field-address1-streetNo' if address1 & streetNo is in the same field (handled in fillInAddress())
    },
    locality: {
        // city
        value: 'long_name',
        selector: '.gpac-field-city'
    },
    administrative_area_level_1: {
        // state code
        value: 'short_name',
        selector: '.gpac-field-state'
    },
    country: {
        // country code
        value: 'short_name',
        selector: '.gpac-field-country'
    },
    postal_code: {
        value: 'short_name',
        selector: '.gpac-field-postal'
    }
};
// "city" (`locality`) may also be `postal_town` or `sublocality_level_1` for some countries
// see also https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform
config.neighborhood = config.locality;
config.postal_town = config.locality;
config.sublocality_level_1 = config.locality;

var configData = getConfigData();

// get server-side config from DOM
function getConfigData() {
    var configElement = document.getElementById('gpac-config');
    return configElement && configElement.dataset;
}

// get options object for google places service requests
function getOptions() {
    var options = {
        types: ['address'] // 'geocode' delivers e.g. cities too, this seems more suitable
    };
    var userCoords;
    var userLocation;

    // get server-side config
    if (configData) {
        // check for country code, restrict search to current country
        if (configData.country) {
            options.componentRestrictions = {country: configData.country};
        }
        // check for rough geolocation
        if (configData.location) {
            try {
                userCoords = JSON.parse(configData.location);
            } catch (e) {
                /* no valid location */
            }
            if (userCoords && userCoords.lat && userCoords.lng) {
                // geolocation found, bias results
                userLocation = new google.maps.Circle({
                    // eslint-disable-line no-undef
                    center: userCoords,
                    radius: 20000 // in meters // keep value high as IP geolocation can be off even 200km+ sometimes
                });
                // set bounds on autocomplete options
                options.bounds = userLocation && userLocation.getBounds();
            }
        }
    }

    return options;
}

// method to parse the response and populate the address input fields
function fillInAddress(autocomplete, inputField) {
    var $addressStreetNoField;
    var combinedAddress;
    var addressComponents = {};
    var stateMap;
    // Get the place details from the autocomplete object.
    var place = autocomplete.getPlace();
    // get parent form
    var $parentForm = $(inputField).parents('fieldset');
    if (!$parentForm.length) {
        $parentForm = $(inputField).parents('form');
    }

    // Reset zip code
    $(config.postal_code.selector, $parentForm).val('');

    if (place && place.address_components && place.address_components.length) {
        // loop response
        place.address_components.forEach(function (addressComponent) {
            var newValue;
            var $inputToFill;
            addressComponent.types.forEach(type => {
                if (type) {
                    if (type == 'postal_code' && addressComponent.types.includes('postal_code_prefix')) {
                        addressComponents[type] = '';
                    } else {
                        if (config[type]) {
                            // get address component from response
                            newValue = addressComponent[config[type].value];
                            $inputToFill = $parentForm.find(config[type].selector);
                            if (newValue && $inputToFill && $inputToFill.length) {
                                $inputToFill.val(newValue).trigger('change', true);
                            }
                            // store values for handling of special cases below
                            addressComponents[type] = newValue;
                        } else {
                            addressComponents[type] = addressComponent.short_name;
                        }
                    }
                }
            });
        });

        // handle address1+streetNo in same field
        $addressStreetNoField = $parentForm.find('.gpac-field-address1-streetNo');
        if ($addressStreetNoField && $addressStreetNoField.length) {
            combinedAddress = addressComponents.route;
            if (addressComponents.street_number && combinedAddress) {
                combinedAddress = addressComponents.street_number + ' ' + combinedAddress;
            }
            if (combinedAddress) {
                $addressStreetNoField.val(combinedAddress).trigger('change', true);
            }
        }

        // handle JP states
        if (addressComponents.administrative_area_level_1 && addressComponents.country === 'JP') {
            if (configData && configData.stateMap) {
                // parse JP states
                try {
                    stateMap = JSON.parse(configData.stateMap);
                } catch (e) {
                    /* no map */
                }

                // map state name from Google to form value
                if (stateMap && stateMap[addressComponents.administrative_area_level_1]) {
                    // set form value
                    $parentForm
                        .find('.gpac-field-state')
                        .val(stateMap[addressComponents.administrative_area_level_1])
                        .trigger('change');
                }
            }
        }
    }
}

// main method for initalising a single input field (may be called directly e.g. when form was
// hidden initially or loaded via AJAX).
function initInputField(inputField, ignoreExistingValue) {
    var $inputParent;
    var $inputCountry;
    var $inputField;

    // Create the autocomplete object, restricting the search predictions to
    // address location types.
    var autocomplete;
    var placesService;
    var initListener;
    var options = getOptions();

    // fix input
    if (typeof inputField === 'string') {
        // selector was passed
        inputField = document.querySelector(inputField); // eslint-disable-line no-param-reassign
    }
    if (inputField && inputField.jquery && inputField.length) {
        // jQuery object was passed
        inputField = inputField[0]; // eslint-disable-line no-param-reassign
    }
    if (typeof google === 'undefined' || !inputField) {
        return;
    }
    $inputField = $(inputField);

    // check if our field has a value, don't initialise prefilled forms
    if (inputField.value && !ignoreExistingValue) {
        // attach a listener to init field on change only, and only once
        initListener = function () {
            $inputField.off('change textInput input', initListener);
            initInputField(inputField, true);
        };
        $inputField.on('change textInput input', initListener);
        return;
    }

    // change country restrictions for multi-country forms when country is changed
    $inputParent = $inputField.parents('fieldset');
    if (!$inputParent.length) {
        $inputParent = $inputField.parents('form');
    }
    $inputCountry = $inputParent.find('.gpac-field-country');

    if ($inputCountry.length) {
        // attach listener for country change
        $inputCountry.on('change', function () {
            var countryValue = $(this).val();
            if (autocompleteMap[inputField.id] && countryValue) {
                // update country restriction
                autocompleteMap[inputField.id].setComponentRestrictions({
                    country: countryValue
                });
            }
        });
        // reset initial country restriction
        if ($inputCountry.val()) {
            options.componentRestrictions = {country: $inputCountry.val()};
        }
    }

    // init autocomplete widget for field
    autocomplete = new google.maps.places.Autocomplete(inputField, options); // eslint-disable-line no-undef
    placesService = new google.maps.places.PlacesService($inputParent.find(config.street_number.selector)[0]);

    $inputParent.on(
        'input',
        config.street_number.selector,
        debounce(function () {
            findPlaceFromQuery($inputParent, placesService);
        }, 400)
    );

    // Avoid paying for data that you don't need by restricting the set of
    // place fields that are returned to just the address components.
    autocomplete.setFields(['address_component']);

    // When the user selects an address from the drop-down, populate the
    // address fields in the form.
    var autocompleteListener = autocomplete.addListener('place_changed', function () {
        fillInAddress(autocomplete, inputField);

        if ($(config.street_number.selector).val()) {
            findPlaceFromQuery($inputParent, placesService);
        }
    });

    // store autocomplete object for later manipulation
    if (inputField.id) {
        autocompleteMap[inputField.id] = autocomplete;
    }

    // Prevent form validation/submission when selecting a suggestion via keyboard
    $('.gpac-input').on('keypress keydown keyup', function (e) {
        if ($(this).is(':-webkit-autofill')) {
            google.maps.event.removeListener(autocompleteListener);
            google.maps.event.clearInstanceListeners(autocomplete);
            $('.pac-container').remove();
        }

        if (e.which == 13 && $('.pac-container').is(':visible')) {
            e.preventDefault();
            e.stopPropagation();
        }
    });
}

//  placesService -> findPlaceFromQuery
function findPlaceFromQuery($inputParent, placesService) {
    var streetNo = $(config.street_number.selector).val();
    var address = $(config.route.selector, $inputParent).val();
    var city = $(config.locality.selector, $inputParent).val();
    var state = $(config.administrative_area_level_1.selector, $inputParent).val();
    var country = $(config.country.selector, $inputParent).val();

    var input = [streetNo + ' ' + address, city, state, country]
        .filter(function (item) {
            return (item || '').trim();
        })
        .join(',');

    if (address && placesService && placesService.findPlaceFromQuery && placesService.getDetails) {
        placesService.findPlaceFromQuery({fields: ['place_id'], query: input}, function (res) {
            if (res && res[0]) {
                placesService.getDetails(
                    {
                        fields: ['address_components'],
                        placeId: res[0].place_id,
                        types: ['postal_code']
                    },
                    function (place) {
                        place.address_components.forEach(function (component) {
                            if (
                                component.types &&
                                component.types.indexOf('postal_code') > -1 &&
                                component.long_name &&
                                !component.types.includes('postal_code_prefix')
                            ) {
                                $inputParent.find('.gpac-field-postal').val(component.long_name);
                            } else if (component.types.includes('postal_code_prefix')) {
                                $inputParent.find('.gpac-field-postal').val('');
                            }
                        });
                    }
                );
            }
        });
    }
}

function getAutocompleteService() {
    var options = getOptions();
    const autocomplete = new google.maps.places.AutocompleteService();

    return {
        getPlacePredictions: function (input) {
            return autocomplete.getPlacePredictions({
                input,
                ...options
            });
        }
    };
}

// shorthand for initInputField, taking a parent jQuery object as param
// (e.g. a form or fieldset) and searching it for the input to initialise.
// used mainly to "manually" init single inputs on certain events, like in .../billingshipping.js
function initAutocompleteForArea($parentArea) {
    var $inputField = $parentArea.find('input.gpac-input');
    if ($inputField && $inputField.length) {
        initInputField($inputField[0]);
    }
}

// initialises all visible inputfields flagged for autocomplete (".gpac-input"),
// called by init() when the GMaps library is available
function initAutocomplete() {
    var inputFields;
    var i;

    if (typeof google !== 'undefined') {
        // look up all visible flagged inputfields and initialise each of them
        inputFields = document.querySelectorAll('.gpac-input');
        if (inputFields && inputFields.length) {
            // IE11 doesn't support NodeList.forEach()
            // https://caniuse.com/#feat=mdn-api_nodelist_foreach
            for (i = 0; i < inputFields.length; i++) {
                if ($(inputFields[i]).is(':visible')) {
                    initInputField(inputFields[i]);
                }
            }
        }
    }
}

// Main init method, utilising initAutocomplete().
//
// Using the native GMaps-callback leads to race conditions when init()
// wasn't executed yet but the GMaps library loaded quicker from cache,
// so we check recursively for GMaps instead.
function init() {
    // config
    var retryCount = 0;
    var maxRetryCount = 5;
    var retryIntervalMs = 1000;

    function tryInit() {
        if (typeof google !== 'undefined') {
            initAutocomplete();
        } else {
            setTimeout(function () {
                if (retryCount < maxRetryCount) {
                    retryCount++;
                    tryInit();
                }
            }, retryIntervalMs);
        }
    }

    tryInit();
}

module.exports = {
    init: init,
    initInputField: initInputField,
    initAutocompleteForArea: initAutocompleteForArea,
    getAutocompleteService: getAutocompleteService,
    enabled: configData ? configData.enabled : false
};
